import {
  getPlayerState,
  qualifiedRecordingName,
  useGlobalPlayer,
} from "@bandwith/recordings";
import { formatDuration } from "@bandwith/time";

/** Format a timestamp reference for embedding in feedback text */
export function formatTimestampReference(
  name: string,
  timeSeconds: number,
): string {
  return `[${name} @ ${formatDuration(timeSeconds)}] `;
}

/**
 * What playing track a timestamp reference may point at: any take of one
 * song (song pages), or an explicit set of recordings (setlist pages, where
 * the linkable takes span many songs).
 */
export type TimestampTarget = { songId: string } | { recordingIds: string[] };

/**
 * Hook for managing audio timestamp references in feedback text.
 * Reads current playback time from the global player store and returns
 * a function to get a formatted reference string for insertion into the editor.
 */
export function useTimestampReference(target: TimestampTarget) {
  // Floor the time so we only re-render when the whole second changes
  const { tracks, currentTrackIndex, displayTime } = useGlobalPlayer((s) => ({
    tracks: s.tracks,
    currentTrackIndex: s.currentTrackIndex,
    displayTime: Math.floor(s.currentTime),
  }));
  const currentTrack =
    currentTrackIndex !== null ? tracks[currentTrackIndex] : null;

  const isPlayingTarget =
    currentTrack != null &&
    ("songId" in target
      ? currentTrack.songId === target.songId
      : target.recordingIds.includes(currentTrack.id));

  /** Get a formatted timestamp reference string, or null if not playing the target */
  const getTimestampReference = () => {
    if (!isPlayingTarget || !currentTrack) return null;
    // On multi-song targets, qualify with the song title so the reference
    // stays unambiguous when takes of different songs share a filename
    const qualified =
      "recordingIds" in target
        ? qualifiedRecordingName(currentTrack.songTitle, currentTrack.filename)
        : null;
    return formatTimestampReference(
      qualified ?? currentTrack.filename,
      getPlayerState().currentTime,
    );
  };

  return {
    currentTime: displayTime,
    isPlayingTarget,
    getTimestampReference,
  };
}
